"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { cn } from "../../lib/utils";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";

// CONTAINER STYLING
const chartContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-2xl bg-card p-4 text-card-foreground ring-1 ring-foreground/10",
          "hover:ring-foreground/20",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

// TITLE STYLING
const chartTitleVariants = cva("font-semibold tracking-tight", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// DESCRIPTION STYLING
const chartDescriptionVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface ChartItemProps
  extends VariantProps<typeof chartContainerVariants> {
  // Accessibility
  ariaLabel?: string;
  aspectRatio?: "video" | "square" | "auto";

  // Styling
  className?: string;
  classNames?: {
    container?: string;
    header?: string;
    title?: string;
    description?: string;
    chart?: string;
  };
  config: ChartConfig;
  // Chart configuration
  data: Record<string, unknown>[];

  // Chart options
  dataKeys?: string[];
  description?: string;

  // Chart dimensions
  height?: number | string;

  // Interaction
  onClick?: () => void;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  size?: "sm" | "md" | "lg";

  // Labels
  title?: string;

  // Styling variants
  variant?: "card" | "outline" | "ghost" | "minimal";
  xAxisKey?: string;
  yAxisWidth?: number;
}

export function ChartItem({
  data,
  config,
  dataKeys,
  xAxisKey = "name",
  showGrid = true,
  showXAxis = true,
  showYAxis = false,
  showLegend = false,
  showTooltip = true,
  title,
  description,
  variant = "card",
  size = "md",
  height,
  aspectRatio = "video",
  onClick,
  ariaLabel,
  className,
  classNames,
  yAxisWidth,
}: ChartItemProps) {
  const chartDataKeys =
    dataKeys ?? Object.keys(config).filter((key) => config[key]?.label);

  const isClickable = Boolean(onClick);

  const containerClassName = cn(
    chartContainerVariants({ variant, size }),
    isClickable && "cursor-pointer",
    classNames?.container,
    className
  );

  const chartClassName = cn(
    aspectRatio === "video" && "aspect-video",
    aspectRatio === "square" && "aspect-square",
    aspectRatio === "auto" && "w-full",
    classNames?.chart
  );

  const chartAriaLabel = ariaLabel ?? (title ? `${title} chart` : "Bar Chart");

  const renderChart = () => (
    <BarChart accessibilityLayer data={data}>
      {showGrid && <CartesianGrid vertical={false} />}
      {showXAxis && (
        <XAxis
          axisLine={false}
          dataKey={xAxisKey}
          tickLine={false}
          tickMargin={10}
        />
      )}
      {showYAxis && (
        <YAxis
          axisLine={false}
          tickLine={false}
          tickMargin={4}
          width={yAxisWidth}
        />
      )}
      {showTooltip && (
        <ChartTooltip content={<ChartTooltipContent />} cursor={false} />
      )}
      {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      {chartDataKeys.map((key) => (
        <Bar dataKey={key} fill={`var(--color-${key})`} key={key} radius={4} />
      ))}
    </BarChart>
  );

  const content = (
    <>
      {(title ?? description) && (
        <div
          className={cn(
            "flex flex-col items-start justify-between gap-1",
            classNames?.header
          )}
        >
          {title && (
            <div
              className={cn(chartTitleVariants({ size }), classNames?.title)}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className={cn(
                chartDescriptionVariants({ size }),
                classNames?.description
              )}
            >
              {description}
            </div>
          )}
        </div>
      )}

      <ChartContainer
        aria-label={chartAriaLabel}
        className={chartClassName}
        config={config}
        style={height ? { height } : undefined}
      >
        {renderChart()}
      </ChartContainer>
    </>
  );

  if (isClickable) {
    return (
      <button className={containerClassName} onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  return <div className={containerClassName}>{content}</div>;
}

export interface ChartCollectionProps
  extends VariantProps<typeof chartContainerVariants> {
  className?: string;
  columns?: 1 | 2 | 3;
  gap?: "sm" | "md" | "lg";
  items: ChartItemProps[];

  layout?: "stack" | "grid";

  onItemClick?: (item: ChartItemProps, index: number) => void;
  size?: "sm" | "md" | "lg";

  variant?: "card" | "outline" | "ghost" | "minimal";
}

const chartCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 md:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
  },
});

export function ChartCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  variant = "card",
  size = "md",
  onItemClick,
  className,
}: ChartCollectionProps) {
  return (
    <div
      className={cn(
        chartCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <ChartItem
          key={item.title ?? `chart-${index}`}
          onClick={
            onItemClick
              ? () => onItemClick(item as ChartItemProps, index)
              : item.onClick
          }
          size={item.size ?? size}
          variant={item.variant ?? variant}
          {...item}
        />
      ))}
    </div>
  );
}
